import { CartAddEvent } from '@theme/events';

const homeRoot = document.querySelector('[data-return-home]');
const assetRoot = homeRoot?.dataset.assetRoot || '';
const assetUrl = (path) => path.startsWith('assets/') ? `${assetRoot}${path.slice(7)}` : path;
const CONFIG = Object.freeze({
  brandName: "Return Hydration",
  productName: "Lemon Drop Electrolyte Powder",
  flavor: "Lemon Drop",
  servings: 30,
  verifiedDate: "2026-08-08",
  verifiedDateDisplay: "August 8, 2026",
  shopUrl: "https://www.returnhydration.com/products/lemon-drop",
  moneyBackCopy: "Flexible subscription",
  totalElectrolytes: "640 mg electrolytes",
  sugarCopy: "0 g sugar",
  caffeineCopy: "Caffeine-free",
  waterAmount: "12–16 oz (350–500 mL)",
  shippingCopy: "Free shipping on every subscription delivery.",
  cancellationCopy: "Skip, pause, or cancel before the next renewal through your account or support.",
  shippingShort: "Free shipping",
  cancellationShort: "Cancel anytime",
  contact: {
    email: "hello@returnhydration.com",
    phone: "(833) 373-8876",
    phoneHref: "+18333738876"
  },
  links: {
    shop: "https://www.returnhydration.com/products/lemon-drop",
    quiz: "https://www.returnhydration.com/pages/return-hydration-quiz",
    subscription: "https://shopify.com/68217798744/account/pages/6971b1a1-27f6-4c27-b8b0-3009fd3b921d",
    shipping: "https://www.returnhydration.com/policies/shipping-policy",
    refund: "https://www.returnhydration.com/policies/refund-policy",
    privacy: "https://www.returnhydration.com/policies/privacy-policy",
    privacyChoices: "https://www.returnhydration.com/pages/data-sharing-opt-out",
    smsTerms: "https://www.returnhydration.com/pages/messaging-consent-privacy",
    terms: "https://www.returnhydration.com/policies/terms-of-service",
    accessibility: "https://www.returnhydration.com/pages/accessibility-at-return-hydration",
    contact: "https://www.returnhydration.com/pages/contact"
  },
  nutrition: {
    servingSize: "2 scoops (9 g)",
    calories: "20",
    totalCarbohydrate: "5 g",
    totalCarbohydrateDv: "2%*",
    dietaryFiber: "1 g",
    dietaryFiberDv: "4%*",
    vitaminC: "300 mg",
    vitaminCDv: "333%*",
    vitaminB12: "15 mcg",
    vitaminB12Dv: "625%*",
    magnesium: "80 mg",
    magnesiumDv: "19%*",
    zinc: "12 mg",
    zincDv: "109%*",
    sodium: "360 mg",
    sodiumDv: "16%*",
    potassium: "200 mg",
    potassiumDv: "4%"
  },
  nutritionRows: [
    { name: "Calories", amount: "20", dv: "" },
    { name: "Total Carbohydrate", amount: "5 g", dv: "2%*" },
    { name: "Dietary Fiber", amount: "1 g", dv: "4%*", indent: true },
    { name: "Vitamin C", detail: "(as ascorbic acid)", amount: "300 mg", dv: "333%*" },
    { name: "Vitamin B12", detail: "(as methylcobalamin)", amount: "15 mcg", dv: "625%*" },
    { name: "Magnesium", detail: "(as magnesium malate)", amount: "80 mg", dv: "19%*" },
    { name: "Zinc", detail: "(as zinc gluconate)", amount: "12 mg", dv: "109%*" },
    { name: "Sodium", amount: "360 mg", dv: "16%*" },
    { name: "Potassium", detail: "(as potassium citrate)", amount: "200 mg", dv: "4%" }
  ],
  
  comparison: {
    verifiedDateDisplay: "August 2026",
    eyebrow: "Compare the formulas",
    heading: "How Return Hydration compares:",
    introLead: "Most hydration brands are designed for heavy sweating.",
    introSupport: "Lemon Drop is made for everyday enjoyment.",
    footnote: "Values reflect official product information reviewed August 2026 and may be subject to change.",
    products: [
      {
        id: "return",
        name: "Return Hydration",
        label: "Return Hydration",
        image: "assets/lemon-drop-upright-cutout-320.webp",
        imageWidth: 320,
        imageHeight: 320,
        sourceUrl: "https://www.returnhydration.com/products/lemon-drop",
        featured: true
      },
      {
        id: "lmnt",
        name: "LMNT Citrus Salt",
        label: "LMNT",
        image: "assets/comparison-lmnt-cutout-240.webp",
        imageWidth: 240,
        imageHeight: 240,
        sourceUrl: "https://drinklmnt.com/products/lmnt-recharge-electrolyte-drink"
      },
      {
        id: "liquid-iv",
        name: "Liquid I.V. Hydration Multiplier Lemon Lime",
        label: "Liquid I.V.",
        image: "assets/comparison-liquid-iv-cutout-240.webp",
        imageWidth: 240,
        imageHeight: 237,
        sourceUrl: "https://drugwholesale.liquid-iv.com/products/liquid-i-v-hydration-multiplier-lemon-lime"
      },
      {
        id: "instant",
        name: "Instant Hydration Premium Electrolyte Drink Mix",
        label: "Instant Hydration",
        image: "assets/comparison-instant-hydration-cutout-240.webp",
        imageWidth: 240,
        imageHeight: 240,
        sourceUrl: "https://instanthydration.com/products/premium-electrolyte-drink-mix"
      }
    ],
    rows: [
      {
        label: "Purpose-built for women 45+",
        values: [
          { type: "yes", label: "Yes" },
          { type: "no", label: "No" },
          { type: "no", label: "No" },
          { type: "no", label: "No" }
        ]
      },
      {
        label: "Sodium per serving",
        values: [
          { type: "number", value: "360", unit: "mg", note: "Lower-sodium hydration" },
          { type: "number", value: "1,000", unit: "mg", note: "Endurance sports" },
          { type: "number", value: "500", unit: "mg", note: "Heavy sweating" },
          { type: "number", value: "500", unit: "mg", note: "Heavy sweating" }
        ]
      },
      {
        label: "Sugar per serving",
        values: [
          { type: "text", value: "0 g" },
          { type: "text", value: "0 g" },
          { type: "text", value: "11 g" },
          { type: "text", value: "0 g" }
        ]
      },
      {
        label: "Vitamin C + zinc",
        values: [
          { type: "yes", label: "Yes" },
          { type: "no", label: "No" },
          { type: "text", value: "Vitamin C only", tone: "partial" },
          { type: "no", label: "No" }
        ]
      },
      {
        label: "Vitamin B12",
        values: [
          { type: "yes", label: "Yes" },
          { type: "no", label: "No" },
          { type: "yes", label: "Yes" },
          { type: "no", label: "No" }
        ]
      },
      {
        label: "Plant-derived soluble fiber",
        values: [
          { type: "yes", label: "Yes" },
          { type: "no", label: "No" },
          { type: "no", label: "No" },
          { type: "no", label: "No" }
        ]
      }
    ]
  },
  otherIngredients: "Citric Acid, Sodium Citrate, Soluble Tapioca Fiber, Natural Flavor, Organic Monk Fruit Extract, Silicon Dioxide, Organic Stevia Extract.",
  allergenStatement: "Produced in a facility that also processes milk, soy, wheat, and tree nuts.",
  quality: {
    headline: "Premium ingredients.",
    headlineAccent: "Nothing to hide.",
    subcopy: "Made locally in USA to ensure the highest quality possible.",
    callouts: [
      { icon: "factory", title: "Made in USA", copy: "Manufactured in the USA under strict quality and testing requirements" },
      { icon: "leaf", title: "Natural Lemon Flavor", copy: "Bright lemon flavor that makes your water taste delicious." },
      { icon: "shield", title: "Quality Testing", copy: "Testing covers key nutrients, heavy metals, and microbiological limits." },
      { icon: "seal", title: "Tamper-Evident Seals", copy: "Each tub is made with an induction seal to lock in freshness." }
    ]
  },
  preparation: {
    neutral: "Mix as directed with 12–16 oz (350–500 mL) of cold water and stir or shake until dissolved.",
    reviewNote: "The live page says one to two scoops while the Supplement Facts defines a full serving as two scoops. Exact scoop copy is intentionally omitted from promotional instructions pending review."
  },
  disclaimers: {
    fda: "These statements have not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease.",
    educational: "General information only and not medical advice. Individual hydration needs vary.",
    creator: "Featured videos may include paid, gifted or creator relationships. Material connections are disclosed where applicable. Individual experiences are personal and may not reflect typical results."
  },
  testimonials: [
    { name: "Lisa", state: "Texas", quote: "It’s simple, it’s easy, and it fits naturally into my day.", video: "assets/return-ugc-lisa.mp4", poster: "assets/return-ugc-lisa-thumbnail-600-v2.webp" },
    { name: "Cheryl", state: "Nevada", quote: "It’s become one of those small daily habits that actually feels good.", video: "assets/return-ugc-cheryl.mp4", poster: "assets/return-ugc-cheryl-thumbnail-600-v2.webp" },
    { name: "Christie", state: "Ohio", quote: "I feel more hydrated, with steadier energy throughout the day.", video: "assets/return-ugc-christie.mp4", poster: "assets/return-ugc-christie-thumbnail-600-v2.webp" },
    { name: "Monica", state: "Missouri", quote: "I’m feeling more steady energy throughout the day!", video: "assets/return-ugc-monica.mp4", poster: "assets/return-ugc-monica-thumbnail-600-v2.webp" },
    { name: "Lois", state: "Illinois", quote: "Your body might just need a different kind of hydration support.", video: "assets/return-ugc-lois.mp4", poster: "assets/return-ugc-lois-thumbnail-600-v3.webp" }
  ],
  faqs: [
    {
      question: "Who is Lemon Drop made for?",
      answer: "Return was created for women 45+ who want balanced everyday hydration, targeted nutrients, and a light flavor that is easy to keep drinking.†"
    },
    {
      question: "What does it taste like?",
      answer: "Bright, lightly sweet lemon with a clean finish, not the heavy, salty taste of a traditional sports drink."
    },
    {
      question: "Does it contain sugar or caffeine?",
      answer: "Lemon Drop has 0 g sugar and no caffeine."
    },
    {
      question: "How do I prepare it?",
      answer: "Mix Lemon Drop with 12–16 oz (350–500 mL) of cold water, then stir and enjoy. Start with 1 scoop and work up to the full 2 scoops depending on your taste preference."
    },
    {
      question: "Can I drink it every day?",
      answer: "Yes! Return is designed for everyday use. If you are pregnant or nursing, taking medication, or managing a health condition, ask your healthcare professional whether this product is appropriate for you."
    },
    {
      question: "How is it sweetened?",
      answer: "Lemon Drop is sweetened with organic monk fruit extract and organic stevia extract. There is no sugar in the formula"
    },
    {
      question: "What allergens should I know about?",
      answer: "Return Hydration is produced in a facility that also processes milk, soy, wheat, and tree nuts."
    },
    {
      question: "How does the subscription work?",
      answer: "Choose monthly delivery to save $10 on each order and get free shipping. Skip, pause, or cancel before your next renewal through your customer account or by contacting support."
    }
  ]
});

window.RETURN_LANDING_CONFIG = CONFIG;

const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
const getConfigValue = (path) => path.split(".").reduce((value, key) => value?.[key], CONFIG);

function hydrateConfig() {
  document.querySelectorAll("[data-config]").forEach((element) => {
    const value = getConfigValue(element.dataset.config);
    if (value !== undefined) element.textContent = value;
  });
  document.querySelectorAll("[data-config-href]").forEach((element) => {
    const value = getConfigValue(element.dataset.configHref);
    if (value) element.href = value;
  });
  document.querySelectorAll("[data-shop-link]").forEach((element) => {
    element.href = CONFIG.shopUrl;
  });
  document.querySelectorAll("[data-email-link]").forEach((element) => {
    element.href = `mailto:${CONFIG.contact.email}`;
  });
  document.querySelectorAll("[data-phone-link]").forEach((element) => {
    element.href = `tel:${CONFIG.contact.phoneHref}`;
  });
}

function renderNutrition() {
  const host = document.querySelector("[data-nutrition-rows]");
  if (!host) return;
  host.innerHTML = CONFIG.nutritionRows.map((row) => `
    <div class="facts-row${row.indent ? " indent" : ""}" role="row">
      <div role="cell"><strong>${row.name}</strong>${row.detail ? ` <small>${row.detail}</small>` : ""}: ${row.amount}</div>
      <span role="cell">${row.dv}</span>
    </div>
  `).join("");
}

function renderGlassComparisons() {
  const roots = document.querySelectorAll("[data-return-glass-comparison]");
  if (!roots.length) return;

  const comparison = CONFIG.comparison;
  const productMap = new Map(comparison.products.map((product, index) => [product.id, { ...product, index }]));
  const orderedProducts = ["return", "lmnt", "liquid-iv", "instant"].map((id) => productMap.get(id)).filter(Boolean);

  const renderValue = (value) => {
    if (value.type === "yes") {
      return '<span class="return-comparison-glass__dot" aria-hidden="true"></span><span class="visually-hidden">Yes</span>';
    }
    if (value.type === "no") {
      return '<span class="return-comparison-glass__no" aria-hidden="true">&times;</span><span class="visually-hidden">No</span>';
    }
    if (value.type === "number") {
      return `<span class="return-comparison-glass__value-stack"><span class="return-comparison-glass__value-main">${value.value} ${value.unit}</span><span class="return-comparison-glass__value-note">${value.note}</span></span>`;
    }
    return `<span>${value.value}</span>`;
  };

  const getValue = (row, product) => row.values[product.index];
  const brand = (label) => `<span class="return-comparison-glass__brand">${label}</span>`;

  roots.forEach((root) => {
    const desktop = root.querySelector("[data-comparison-glass-desktop]");
    if (!(desktop instanceof HTMLElement)) return;

    root.querySelectorAll("[data-comparison-copy]").forEach((element) => {
      const value = comparison[element.dataset.comparisonCopy];
      if (value) element.textContent = value;
    });

    const desktopHeader = `
      <div class="return-comparison-glass__desktop-row" role="row">
        <div class="return-comparison-glass__label return-comparison-glass__header-label" role="columnheader" aria-label="Comparison criteria"></div>
        ${orderedProducts.map((product) => `<div class="return-comparison-glass__value${product.id === "return" ? " return-comparison-glass__return" : ""}" role="columnheader">${brand(product.label)}</div>`).join("")}
      </div>
    `;
    const desktopRows = comparison.rows.map((row) => `
      <div class="return-comparison-glass__desktop-row" role="row">
        <div class="return-comparison-glass__label" role="rowheader">${row.label}</div>
        ${orderedProducts.map((product) => `<div class="return-comparison-glass__value${product.id === "return" ? " return-comparison-glass__return" : ""}" role="cell">${renderValue(getValue(row, product))}</div>`).join("")}
      </div>
    `).join("");
    desktop.innerHTML = desktopHeader + desktopRows;
  });
}

const QUALITY_ICONS = Object.freeze({
  factory: '<svg viewBox="0 0 48 48" aria-hidden="true"><path d="M7 41V19l12 7v-7l12 7V12h10v29H7Z"/><path d="M13 34h4m6 0h4m6 0h4M35 12V7h4v5"/></svg>',
  leaf: '<svg viewBox="0 0 48 48" aria-hidden="true"><path d="M39 9C24 10 11 17 10 30c0 6 4 10 10 10 13-1 20-14 19-31Z"/><path d="M11 40c4-10 11-17 22-24"/></svg>',
  shield: '<svg viewBox="0 0 48 48" aria-hidden="true"><path d="M24 5 39 11v11c0 10-6 17-15 21C15 39 9 32 9 22V11l15-6Z"/><path d="m17 24 5 5 10-11"/></svg>',
  seal: '<svg viewBox="0 0 48 48" aria-hidden="true"><path d="M10 15h28v26H10zM8 9h32v6H8z"/><path d="M18 9V6h12v3m-3 18-3-2-3 2 1-4-3-3 4-.5 1-4 1 4 4 .5-3 3 1 4Z"/></svg>'
});

function renderQualityCallouts() {
  const host = document.querySelector("[data-quality-callouts]");
  if (!host) return;
  host.innerHTML = CONFIG.quality.callouts.map((item) => `
    <article class="quality-callout">
      <div class="quality-icon">${QUALITY_ICONS[item.icon] || QUALITY_ICONS.shield}</div>
      <div><h3>${item.title}</h3><p>${item.copy}</p></div>
    </article>
  `).join("");
}

function renderTestimonials() {
  const track = document.querySelector("[data-review-track]");
  if (!track) return;
  track.innerHTML = CONFIG.testimonials.map((review, index) => `
    <article class="review-card" role="group" aria-roledescription="slide" aria-label="${review.name}, testimonial ${index + 1} of ${CONFIG.testimonials.length}">
      <div class="review-media">
        <video class="review-video" width="720" height="1280" playsinline preload="none"${review.poster ? ` data-poster="${assetUrl(review.poster)}"` : ""} aria-label="${review.name} from ${review.state} sharing her Return Hydration experience">
          <source src="${assetUrl(review.video)}" type="video/mp4">
          Your browser does not support video playback.
        </video>
        ${review.thumbnailNeeded ? `<div class="review-thumbnail-needed" role="img" aria-label="Thumbnail needed for ${review.name}’s video testimonial"><span>THUMBNAIL NEEDED</span></div>` : ""}
        <div class="review-overlay">
          <blockquote>“${review.quote}”<sup>§</sup></blockquote>
          <footer><strong>${review.name}</strong><span>${review.state}</span></footer>
        </div>
        <button class="review-play" type="button" aria-label="Play ${review.name}’s video testimonial">
          <span class="review-play-icon" aria-hidden="true"></span>
        </button>
      </div>
    </article>
  `).join("");
}

function alignTestimonialForPlayback(card) {
  if (!card || !window.matchMedia("(max-width: 900px)").matches) return;
  const track = card.closest("[data-review-track]");
  if (track) {
    const trackRect = track.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const paddingStart = Number.parseFloat(getComputedStyle(track).paddingLeft) || 0;
    const targetLeft = Math.max(0, track.scrollLeft + cardRect.left - trackRect.left - paddingStart);
    track.scrollTo({ left: targetLeft, behavior: reducedMotion.matches ? "auto" : "smooth" });
  }

  const header = document.querySelector("[data-rh3-header]");
  const headerBottom = Math.max(0, header?.getBoundingClientRect().bottom || 0);
  const safeTop = headerBottom + 12;
  const cardTop = card.getBoundingClientRect().top;
  const targetTop = Math.max(0, window.scrollY + cardTop - safeTop);
  if (Math.abs(cardTop - safeTop) > 2) {
    window.scrollTo({ top: targetTop, behavior: reducedMotion.matches ? "auto" : "smooth" });
  }
}

function setupTestimonialVideos() {
  const videos = [...document.querySelectorAll(".review-video")];
  if (!videos.length) return;
  videos.forEach((video) => {
    const card = video.closest(".review-card");
    const playButton = card?.querySelector(".review-play");
    let transferFocusOnPlay = false;
    playButton?.addEventListener("click", async (event) => {
      const pointerActivation = event.detail !== 0;
      const mobilePointerActivation = pointerActivation && window.matchMedia("(max-width: 900px)").matches;
      transferFocusOnPlay = !pointerActivation;
      alignTestimonialForPlayback(card);
      if (!video.poster && video.dataset.poster) video.poster = video.dataset.poster;
      video.controls = true;
      video.tabIndex = 0;
      try {
        await video.play();
        if (mobilePointerActivation) requestAnimationFrame(() => alignTestimonialForPlayback(card));
      } catch {
        transferFocusOnPlay = false;
        card?.classList.remove("is-playing", "has-started");
        video.controls = false;
        video.removeAttribute("tabindex");
        playButton.removeAttribute("aria-hidden");
        playButton.tabIndex = 0;
      }
    });
    video.addEventListener("play", () => {
      card?.classList.add("is-playing", "has-started");
      playButton?.setAttribute("aria-hidden", "true");
      if (playButton) playButton.tabIndex = -1;
      if (transferFocusOnPlay && document.activeElement === playButton) video.focus({ preventScroll: true });
      transferFocusOnPlay = false;
      videos.forEach((other) => { if (other !== video) other.pause(); });
    });
    video.addEventListener("pause", () => card?.classList.remove("is-playing"));
    video.addEventListener("ended", () => {
      const returnFocus = document.activeElement === video;
      card?.classList.remove("is-playing", "has-started");
      video.controls = false;
      video.removeAttribute("tabindex");
      video.currentTime = 0;
      playButton?.removeAttribute("aria-hidden");
      if (playButton) playButton.tabIndex = 0;
      if (returnFocus) playButton?.focus({ preventScroll: true });
    });
    video.addEventListener("error", () => {
      const returnFocus = document.activeElement === video;
      card?.classList.remove("is-playing", "has-started");
      video.controls = false;
      video.removeAttribute("tabindex");
      playButton?.removeAttribute("aria-hidden");
      if (playButton) playButton.tabIndex = 0;
      if (returnFocus) playButton?.focus({ preventScroll: true });
    });
  });
  if (!("IntersectionObserver" in window)) return;
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) entry.target.pause();
    });
  }, { threshold: .2 });
  videos.forEach((video) => observer.observe(video));
}

function setupDeferredMedia() {
  const images = [...document.querySelectorAll("img[data-deferred-src]")];
  const videos = [...document.querySelectorAll("video[data-poster]")];
  const loadImage = (image) => {
    image.parentElement?.querySelectorAll("source[data-deferred-srcset]").forEach((source) => {
      source.srcset = source.dataset.deferredSrcset;
      source.removeAttribute("data-deferred-srcset");
    });
    if (image.dataset.deferredSrcset) image.srcset = image.dataset.deferredSrcset;
    if (image.dataset.deferredSrc) image.src = image.dataset.deferredSrc;
    image.removeAttribute("data-deferred-src");
    image.removeAttribute("data-deferred-srcset");
  };
  const loadVideoPoster = (video) => {
    if (video.dataset.poster) video.poster = video.dataset.poster;
    video.removeAttribute("data-poster");
  };
  const targets = [...images, ...videos];
  if (!targets.length) {
    window.__returnDeferredMediaReady = true;
    return;
  }
  if (!("IntersectionObserver" in window)) {
    images.forEach(loadImage);
    videos.forEach(loadVideoPoster);
    window.__returnDeferredMediaReady = true;
    return;
  }
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      if (entry.target instanceof HTMLImageElement) loadImage(entry.target);
      else loadVideoPoster(entry.target);
      observer.unobserve(entry.target);
    });
  }, { rootMargin: "1000px 0px", threshold: 0.01 });
  targets.forEach((target) => observer.observe(target));
  window.__returnDeferredMediaReady = true;
}

function renderFaqs() {
  const list = document.querySelector("[data-faq-list]");
  if (!list) return;
  list.innerHTML = CONFIG.faqs.map((item) => `
    <details class="faq-item">
      <summary class="faq-question">
        ${item.question}<span aria-hidden="true">+</span>
      </summary>
      <div class="faq-answer"><div><p>${item.answer}</p></div></div>
    </details>
  `).join("");
}

function setupHeader() {
  const header = document.querySelector("[data-header]");
  const update = () => header?.classList.toggle("is-scrolled", window.scrollY > 18);
  update();
  window.addEventListener("scroll", update, { passive: true });
}

function setupDrawer() {
  const drawer = document.querySelector("#mobile-drawer");
  const toggle = document.querySelector(".menu-toggle");
  const close = document.querySelector(".drawer-close");
  const backdrop = document.querySelector("[data-drawer-backdrop]");
  if (!drawer || !toggle || !close || !backdrop) return;
  let lastFocused = null;
  let closingTimer = null;
  const focusables = () => [...drawer.querySelectorAll('a[href], button:not([disabled])')];

  const setOpen = (open) => {
    window.clearTimeout(closingTimer);
    toggle.setAttribute("aria-expanded", String(open));
    toggle.setAttribute("aria-label", open ? "Close navigation menu" : "Open navigation menu");
    drawer.setAttribute("aria-hidden", String(!open));
    document.body.classList.toggle("is-locked", open);
    if (open) {
      lastFocused = document.activeElement;
      drawer.hidden = false;
      backdrop.hidden = false;
      requestAnimationFrame(() => {
        drawer.classList.add("is-open");
        backdrop.classList.add("is-open");
        close.focus();
      });
    } else {
      drawer.classList.remove("is-open");
      backdrop.classList.remove("is-open");
      lastFocused?.focus();
      closingTimer = window.setTimeout(() => {
        drawer.hidden = true;
        backdrop.hidden = true;
      }, 220);
    }
  };

  toggle.addEventListener("click", () => setOpen(toggle.getAttribute("aria-expanded") !== "true"));
  close.addEventListener("click", () => setOpen(false));
  backdrop.addEventListener("click", () => setOpen(false));
  drawer.querySelectorAll("a").forEach((link) => link.addEventListener("click", () => setOpen(false)));
  document.addEventListener("keydown", (event) => {
    if (drawer.hidden) return;
    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      return;
    }
    if (event.key !== "Tab") return;
    const items = focusables();
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
}

function setupHeroVideo() {
  const video = document.querySelector("[data-hero-video]");
  if (!video) return;
  const frame = video.closest(".video-frame");
  const fallback = frame?.querySelector("[data-video-fallback]");
  const showFallback = () => {
    frame?.classList.add("has-fallback");
    fallback?.removeAttribute("aria-hidden");
  };
  const useVideo = () => {
    if (reducedMotion.matches) {
      video.pause();
      showFallback();
      return;
    }
    frame?.classList.remove("has-fallback");
    fallback?.setAttribute("aria-hidden", "true");
    const attempt = video.play();
    if (attempt?.catch) attempt.catch(showFallback);
  };
  video.addEventListener("error", showFallback, { once: true });
  video.addEventListener("stalled", showFallback, { once: true });
  reducedMotion.addEventListener?.("change", useVideo);
  useVideo();

  if ("IntersectionObserver" in window) {
    const observer = new IntersectionObserver(([entry]) => {
      if (reducedMotion.matches || frame?.classList.contains("has-fallback")) return;
      if (entry.isIntersecting && entry.intersectionRatio > .18) video.play().catch(showFallback);
      else video.pause();
    }, { threshold: [0, .18, .5] });
    observer.observe(video);
  }
}

function setupReveals() {
  const items = document.querySelectorAll(".reveal");
  if (reducedMotion.matches || !("IntersectionObserver" in window)) {
    items.forEach((item) => item.classList.add("is-visible"));
    return;
  }
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add("is-visible");
      observer.unobserve(entry.target);
    });
  }, { threshold: .13, rootMargin: "0px 0px -4%" });
  items.forEach((item) => observer.observe(item));
}

function setupCounters() {
  const counters = document.querySelectorAll("[data-count]");
  counters.forEach((counter) => {
    const configPath = counter.dataset.countConfig;
    if (configPath) {
      counter.dataset.count = String(getConfigValue(configPath));
      counter.textContent = String(getConfigValue(configPath));
    }
  });
  if (!counters.length || reducedMotion.matches || !("IntersectionObserver" in window)) return;
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const element = entry.target;
      const end = Number(element.dataset.count);
      const start = performance.now();
      const duration = 900;
      const animate = (now) => {
        const progress = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        element.textContent = String(Math.round(end * eased));
        if (progress < 1) requestAnimationFrame(animate);
      };
      requestAnimationFrame(animate);
      observer.unobserve(element);
    });
  }, { threshold: .65 });
  counters.forEach((counter) => observer.observe(counter));
}

function setupParallax() {
  const product = document.querySelector("[data-parallax]");
  if (!product || reducedMotion.matches) return;
  let scheduled = false;
  const update = () => {
    const rect = product.getBoundingClientRect();
    const centerOffset = (rect.top + rect.height / 2 - window.innerHeight / 2) / window.innerHeight;
    product.style.transform = `translate3d(0, ${Math.max(-14, Math.min(14, centerOffset * -22))}px, 0)`;
    scheduled = false;
  };
  window.addEventListener("scroll", () => {
    if (!scheduled) {
      scheduled = true;
      requestAnimationFrame(update);
    }
  }, { passive: true });
}

function setupTasteScroll() {
  const section = document.querySelector("[data-taste-scroll]");
  const stage = section?.querySelector("[data-taste-stage]");
  const copy = section?.querySelector("[data-taste-copy]");
  const slices = [...(section?.querySelectorAll("[data-taste-slice]") || [])];
  if (!section || !stage || !copy || !slices.length || reducedMotion.matches) return;

  section.classList.add("is-enhanced");
  const clamp = (value, minimum = 0, maximum = 1) => Math.min(maximum, Math.max(minimum, value));
  const mix = (start, end, progress) => start + (end - start) * progress;
  let ticking = false;

  const render = () => {
    const rect = section.getBoundingClientRect();
    const stickyTop = Number.parseFloat(getComputedStyle(stage).top) || 0;
    const travel = Math.max(1, section.offsetHeight - window.innerHeight + stickyTop);
    const progress = clamp((stickyTop - rect.top) / travel);
    const eased = progress * progress * (3 - 2 * progress);
    const stageWidth = stage.clientWidth;
    const stageHeight = stage.clientHeight;

    slices.forEach((slice) => {
      const x = mix(Number(slice.dataset.sx), Number(slice.dataset.ex), eased) * stageWidth / 100;
      const y = mix(Number(slice.dataset.sy), Number(slice.dataset.ey), eased) * stageHeight / 100;
      const rotation = mix(Number(slice.dataset.sr), Number(slice.dataset.er), eased);
      const scale = mix(Number(slice.dataset.ss), Number(slice.dataset.es), eased);
      slice.style.transform = `translate3d(calc(-50% + ${x}px), calc(-50% + ${y}px), 0) rotate(${rotation}deg) scale(${scale})`;
    });

    const copyProgress = clamp((progress - .28) / .3);
    stage.style.setProperty("--taste-copy-progress", String(copyProgress));
    copy.style.opacity = String(copyProgress);
    copy.style.transform = `translate3d(0, ${mix(28, 0, copyProgress)}px, 0)`;
    ticking = false;
  };

  const requestRender = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(render);
  };

  window.addEventListener("scroll", requestRender, { passive: true });
  window.addEventListener("resize", requestRender, { passive: true });
  render();
}

function setupPurchaseOptions() {
  const buyBox = document.querySelector('[data-buy-box]');
  if (!(buyBox instanceof HTMLElement)) return;
  const options = [...buyBox.querySelectorAll('[data-return-purchase-option]')];
  const price = buyBox.querySelector('[data-return-cta-price]');
  const ctaLabel = buyBox.querySelector('[data-return-cta-label]');
  const primaryCta = buyBox.querySelector('[data-checkout-subscription]');
  const status = buyBox.querySelector('[data-buy-status]');
  const subscriptionSupport = buyBox.querySelector('[data-return-subscription-support]');
  const onceSupport = buyBox.querySelector('[data-return-once-support]');
  const selectedPlan = () => options.find((input) => input.checked && !input.disabled) || options.find((input) => !input.disabled);
  const setBusy = (busy) => {
    if (!(primaryCta instanceof HTMLButtonElement)) return;
    primaryCta.disabled = busy;
    primaryCta.toggleAttribute('aria-busy', busy);
  };
  const addToCart = async ({ quantity, sellingPlanId = '' }) => {
    const variantId = Number(buyBox.dataset.variantId);
    const cartAddUrl = buyBox.dataset.cartAddUrl;
    if (!variantId || !cartAddUrl || !Number.isInteger(quantity) || quantity < 1) return;
    const item = { id: variantId, quantity };
    if (sellingPlanId) item.selling_plan = Number(sellingPlanId);
    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((element) => element.dataset.sectionId)
      .filter(Boolean);
    const payload = { items: [item] };
    if (sectionIds.length) {
      payload.sections = sectionIds;
      payload.sections_url = window.location.pathname;
    }
    setBusy(true);
    if (status) status.textContent = '';
    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Unable to add this item to your cart.');
      buyBox.dispatchEvent(new CartAddEvent(data, String(variantId), {
        source: 'lemon-drop-home',
        itemCount: quantity,
        variantId: String(variantId),
        sections: data.sections
      }));
      document.querySelector('cart-drawer-component')?.open();
      if (status) status.textContent = 'Added to your cart.';
    } catch (error) {
      if (status) status.textContent = error instanceof Error ? error.message : 'Unable to add this item to your cart.';
    } finally {
      setBusy(false);
    }
  };
  const update = () => {
    const plan = selectedPlan();
    if (!(plan instanceof HTMLInputElement)) return;
    if (!plan.checked) plan.checked = true;
    options.forEach((input) => input.closest('[data-return-purchase-card]')?.classList.toggle('is-selected', input === plan));
    const isSubscription = plan.dataset.purchaseMode === 'subscription';
    if (price) price.textContent = plan.dataset.total || '';
    if (ctaLabel) ctaLabel.textContent = plan.dataset.cta || (isSubscription ? 'Start my subscription' : 'Buy once');
    if (subscriptionSupport instanceof HTMLElement) subscriptionSupport.hidden = !isSubscription;
    if (onceSupport instanceof HTMLElement) onceSupport.hidden = isSubscription;
    if (primaryCta instanceof HTMLButtonElement) primaryCta.disabled = plan.disabled;
  };
  options.forEach((input) => input.addEventListener("change", update));
  primaryCta?.addEventListener('click', () => {
    const plan = selectedPlan();
    if (!(plan instanceof HTMLInputElement)) return;
    addToCart({
      quantity: Number(plan.dataset.quantity) || 1,
      sellingPlanId: plan.dataset.purchaseMode === 'subscription' ? plan.dataset.sellingPlan || '' : ''
    });
  });

  const flavorButtons = [...buyBox.querySelectorAll('[data-return-flavor]')];
  const purchaseState = buyBox.querySelector('[data-return-purchase-state]');
  const comingState = buyBox.querySelector('[data-return-coming-state]');
  const selectFlavor = (flavor) => {
    flavorButtons.forEach((button) => {
      const selected = button.dataset.returnFlavor === flavor;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
    });
    if (purchaseState instanceof HTMLElement) purchaseState.hidden = flavor !== 'lemon';
    if (comingState instanceof HTMLElement) comingState.hidden = flavor !== 'cola';
    if (status) status.textContent = '';
  };
  flavorButtons.forEach((button) => button.addEventListener('click', () => selectFlavor(button.dataset.returnFlavor)));

  const notifyTermsTrigger = buyBox.querySelector('[data-return-notify-terms-open]');
  const notifyTermsDialog = buyBox.querySelector('[data-return-notify-terms-dialog]');
  const notifyTermsClose = buyBox.querySelector('[data-return-notify-terms-close]');
  if (notifyTermsTrigger instanceof HTMLButtonElement && notifyTermsDialog instanceof HTMLDialogElement && notifyTermsClose instanceof HTMLButtonElement) {
    notifyTermsTrigger.addEventListener('click', () => {
      if (typeof notifyTermsDialog.showModal === 'function') notifyTermsDialog.showModal();
      notifyTermsClose.focus({ preventScroll: true });
    });
    notifyTermsClose.addEventListener('click', () => notifyTermsDialog.close());
    notifyTermsDialog.addEventListener('click', (event) => {
      if (event.target === notifyTermsDialog) notifyTermsDialog.close();
    });
    notifyTermsDialog.addEventListener('close', () => notifyTermsTrigger.focus({ preventScroll: true }));
  }
  update();
}

function setupReviewCarousel() {
  const track = document.querySelector("[data-review-track]");
  const previous = document.querySelector("[data-review-prev]");
  const next = document.querySelector("[data-review-next]");
  if (!track || !previous || !next) return;
  const controls = previous.closest(".carousel-controls");
  const cards = [...track.querySelectorAll(".review-card")];
  const edgeTolerance = 2;
  let stateFrame = 0;
  const step = () => track.querySelector(".review-card")?.getBoundingClientRect().width + 15 || 400;
  const move = (direction) => track.scrollBy({ left: direction * step(), behavior: reducedMotion.matches ? "auto" : "smooth" });
  const updateControls = () => {
    stateFrame = 0;
    const trackRect = track.getBoundingClientRect();
    const firstRect = cards[0]?.getBoundingClientRect();
    const lastRect = cards.at(-1)?.getBoundingClientRect();
    const canMovePrevious = Boolean(firstRect && firstRect.left < trackRect.left - edgeTolerance);
    const canMoveNext = Boolean(lastRect && lastRect.right > trackRect.right + edgeTolerance);
    previous.hidden = !canMovePrevious;
    next.hidden = !canMoveNext;
    if (controls) controls.hidden = !canMovePrevious && !canMoveNext;
    if (!canMovePrevious && document.activeElement === previous && canMoveNext) next.focus({ preventScroll: true });
    if (!canMoveNext && document.activeElement === next && canMovePrevious) previous.focus({ preventScroll: true });
  };
  const requestControlUpdate = () => {
    if (stateFrame) return;
    stateFrame = requestAnimationFrame(updateControls);
  };
  previous.addEventListener("click", () => move(-1));
  next.addEventListener("click", () => move(1));
  track.addEventListener("scroll", requestControlUpdate, { passive: true });
  track.addEventListener("keydown", (event) => {
    if (event.key === "ArrowLeft") { event.preventDefault(); move(-1); }
    if (event.key === "ArrowRight") { event.preventDefault(); move(1); }
    if (event.key === "Home") { event.preventDefault(); track.scrollTo({ left: 0, behavior: reducedMotion.matches ? "auto" : "smooth" }); }
    if (event.key === "End") { event.preventDefault(); track.scrollTo({ left: track.scrollWidth, behavior: reducedMotion.matches ? "auto" : "smooth" }); }
  });
  let touchStart = null;
  track.addEventListener("touchstart", (event) => { touchStart = event.touches[0]?.clientX ?? null; }, { passive: true });
  track.addEventListener("touchend", (event) => {
    if (touchStart === null) return;
    const delta = touchStart - (event.changedTouches[0]?.clientX ?? touchStart);
    if (Math.abs(delta) > 45) move(delta > 0 ? 1 : -1);
    touchStart = null;
  }, { passive: true });
  if ("ResizeObserver" in window) new ResizeObserver(requestControlUpdate).observe(track);
  else window.addEventListener("resize", requestControlUpdate, { passive: true });
  window.addEventListener("load", requestControlUpdate, { once: true });
  updateControls();
}

function setupMobileCardCarousel({ trackSelector, cardSelector, dotSelector }) {
  const track = document.querySelector(trackSelector);
  const cards = [...(track?.querySelectorAll(cardSelector) || [])];
  const dots = [...document.querySelectorAll(dotSelector)];
  if (!track || cards.length < 2 || dots.length !== cards.length) return;

  const compactLayout = window.matchMedia("(max-width: 640px)");
  let activeIndex = 0;
  let stateFrame = 0;

  const cardStart = (card) => {
    const trackRect = track.getBoundingClientRect();
    return card.getBoundingClientRect().left - trackRect.left + track.scrollLeft;
  };
  const scrollInset = () => Number.parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
  const setActive = (index) => {
    activeIndex = Math.max(0, Math.min(cards.length - 1, index));
    dots.forEach((dot, dotIndex) => {
      const active = dotIndex === activeIndex;
      dot.classList.toggle("is-active", active);
      if (active) dot.setAttribute("aria-current", "step");
      else dot.removeAttribute("aria-current");
    });
  };
  const update = () => {
    stateFrame = 0;
    track.tabIndex = compactLayout.matches ? 0 : -1;
    if (!compactLayout.matches) {
      setActive(0);
      return;
    }
    const closest = cards.reduce((best, card, index) => {
      const distance = Math.abs(cardStart(card) - scrollInset() - track.scrollLeft);
      return distance < best.distance ? { index, distance } : best;
    }, { index: 0, distance: Number.POSITIVE_INFINITY });
    setActive(closest.index);
  };
  const requestUpdate = () => {
    if (stateFrame) return;
    stateFrame = requestAnimationFrame(update);
  };
  const goTo = (index) => {
    const card = cards[index];
    if (!card || !compactLayout.matches) return;
    track.scrollTo({ left: cardStart(card) - scrollInset(), behavior: reducedMotion.matches ? "auto" : "smooth" });
    setActive(index);
  };

  dots.forEach((dot, index) => dot.addEventListener("click", () => goTo(index)));
  track.addEventListener("scroll", requestUpdate, { passive: true });
  track.addEventListener("keydown", (event) => {
    if (!compactLayout.matches || !["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    if (event.key === "Home") goTo(0);
    else if (event.key === "End") goTo(cards.length - 1);
    else goTo(activeIndex + (event.key === "ArrowRight" ? 1 : -1));
  });
  compactLayout.addEventListener?.("change", () => {
    if (!compactLayout.matches) track.scrollTo({ left: 0 });
    update();
  });
  if ("ResizeObserver" in window) new ResizeObserver(requestUpdate).observe(track);
  update();
}

function setupStepCarousel() {
  setupMobileCardCarousel({
    trackSelector: "[data-step-carousel]",
    cardSelector: "[data-step-card]",
    dotSelector: "[data-step-carousel-dot]"
  });
}

function setupBenefitCarousel() {
  setupMobileCardCarousel({
    trackSelector: "[data-benefit-carousel]",
    cardSelector: "[data-benefit-card]",
    dotSelector: "[data-benefit-carousel-dot]"
  });
}

function setupAccessibleScrollRegions() {
  const regions = [...document.querySelectorAll("[data-scroll-region]")];
  if (!regions.length) return;
  const syncRegion = (region) => {
    const scrollable = region.scrollWidth > region.clientWidth + 1;
    region.tabIndex = scrollable ? 0 : -1;
  };
  regions.forEach((region) => {
    region.addEventListener("keydown", (event) => {
      if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
      event.preventDefault();
      const behavior = reducedMotion.matches ? "auto" : "smooth";
      if (event.key === "Home") region.scrollTo({ left: 0, behavior });
      else if (event.key === "End") region.scrollTo({ left: region.scrollWidth, behavior });
      else region.scrollBy({ left: (event.key === "ArrowRight" ? 1 : -1) * region.clientWidth * .82, behavior });
    });
    syncRegion(region);
    if ("ResizeObserver" in window) new ResizeObserver(() => syncRegion(region)).observe(region);
  });
  window.addEventListener("load", () => regions.forEach(syncRegion), { once: true });
}

function setupSupplementFactsDialog() {
  const facts = document.querySelector(".supplement-facts");
  const trigger = document.querySelector("[data-supplement-facts-open]");
  const dialog = document.querySelector("[data-supplement-facts-dialog]");
  const modalBody = document.querySelector("[data-supplement-facts-modal-body]");
  const closeButton = document.querySelector("[data-supplement-facts-close]");
  if (!facts || !trigger || !dialog || !modalBody || !closeButton || typeof dialog.showModal !== "function") return;

  const compactLayout = window.matchMedia("(max-width: 900px)");
  const desktopParent = facts.parentNode;
  const desktopNextSibling = facts.nextSibling;
  const restoreFacts = () => desktopParent.insertBefore(facts, desktopNextSibling);
  const syncLayout = () => {
    if (compactLayout.matches) {
      modalBody.append(facts);
      trigger.hidden = false;
      return;
    }
    if (dialog.open) dialog.close();
    restoreFacts();
    trigger.hidden = true;
  };

  trigger.addEventListener("click", () => {
    dialog.showModal();
    closeButton.focus({ preventScroll: true });
  });
  closeButton.addEventListener("click", () => dialog.close());
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) dialog.close();
  });
  dialog.addEventListener("close", () => {
    if (compactLayout.matches && trigger.offsetParent !== null) trigger.focus({ preventScroll: true });
  });
  compactLayout.addEventListener?.("change", syncLayout);
  syncLayout();
}

function setupStickyCta() {
  const sticky = document.querySelector("[data-sticky-cta]");
  const offer = document.querySelector("[data-home-buybox], [data-buy-box], #offer");
  const hero = document.querySelector(".hero");
  if (!sticky || !offer || !hero || !("IntersectionObserver" in window)) return;
  const visible = { hero: true, offer: false };
  const update = () => sticky.classList.toggle("is-hidden", visible.hero || visible.offer);
  const heroObserver = new IntersectionObserver(([entry]) => {
    visible.hero = entry.isIntersecting;
    update();
  }, { threshold: .01 });
  const offerObserver = new IntersectionObserver(([entry]) => {
    visible.offer = entry.isIntersecting;
    update();
  }, { threshold: .12 });
  heroObserver.observe(hero);
  offerObserver.observe(offer);
  update();
}

hydrateConfig();
renderNutrition();
renderGlassComparisons();
renderQualityCallouts();
renderTestimonials();
renderFaqs();
setupDeferredMedia();
setupHeader();
setupDrawer();
setupHeroVideo();
setupReveals();
setupCounters();
setupTasteScroll();
setupParallax();
setupPurchaseOptions();
setupTestimonialVideos();
setupReviewCarousel();
setupStepCarousel();
setupBenefitCarousel();
setupAccessibleScrollRegions();
setupSupplementFactsDialog();
setupStickyCta();
